/**
 * TanStack Query hooks for Data Discovery
 * Provides connection, scan, and classification operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { isApiError } from '@/lib/api-client'
import { isValidUUID } from '@/lib/discovery-utils'
import { useAuthStore } from '@/stores/auth-store'
import {
  fetchConnections,
  fetchConnection,
  createConnection,
  updateConnection,
  deleteConnection,
  testConnection,
  fetchScans,
  fetchScan,
  startScan,
  fetchScanResults,
  reclassifyScan,
  deleteScan,
  fetchSchemas,
  fetchTables,
  fetchColumns,
} from '@/lib/discovery-api'
import type {
  ConnectionQueryParams,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  ScanQueryParams,
  CreateScanRequest,
  ScanResultsQueryParams,
} from '@/types/discovery'

// ============================================================================
// CACHE KEY FACTORIES
// ============================================================================

export const connectionKeys = {
  all: ['discovery', 'connections'] as const,
  lists: () => [...connectionKeys.all, 'list'] as const,
  list: (params: ConnectionQueryParams) =>
    [...connectionKeys.lists(), params] as const,
  details: () => [...connectionKeys.all, 'detail'] as const,
  detail: (id: string) => [...connectionKeys.details(), id] as const,
  schemas: (id: string) =>
    [...connectionKeys.detail(id), 'schemas'] as const,
  tables: (id: string, schema: string) =>
    [...connectionKeys.detail(id), 'tables', schema] as const,
  columns: (id: string, schema: string, table: string) =>
    [...connectionKeys.detail(id), 'columns', schema, table] as const,
}

export const scanKeys = {
  all: ['discovery', 'scans'] as const,
  lists: () => [...scanKeys.all, 'list'] as const,
  list: (params: ScanQueryParams) => [...scanKeys.lists(), params] as const,
  details: () => [...scanKeys.all, 'detail'] as const,
  detail: (id: string) => [...scanKeys.details(), id] as const,
  results: (id: string, params: ScanResultsQueryParams) =>
    [...scanKeys.detail(id), 'results', params] as const,
}

export const discoveryDashboardKeys = {
  all: ['discovery', 'dashboard'] as const,
}

// ============================================================================
// CONNECTION QUERY HOOKS
// ============================================================================

export function useConnections(params: ConnectionQueryParams = {}) {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: connectionKeys.list(params),
    queryFn: () => fetchConnections(params),
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000,
    enabled: !!tenantId,
  })
}

export function useConnection(id: string | undefined) {
  const normalizedId =
    id && id !== 'undefined' && id !== 'null' && isValidUUID(id) ? id : undefined

  return useQuery({
    queryKey: normalizedId
      ? connectionKeys.detail(normalizedId)
      : ['discovery', 'connections', 'detail', 'disabled'],
    queryFn: () => {
      if (!normalizedId) {
        return Promise.reject(new Error('Connection ID is required'))
      }
      return fetchConnection(normalizedId)
    },
    enabled: !!normalizedId,
    retry: false,
    staleTime: 10 * 1000,
  })
}

// ============================================================================
// CONNECTION MUTATION HOOKS
// ============================================================================

export function useCreateConnection() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: CreateConnectionRequest) => createConnection(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      toast.success('Connection created successfully')
      navigate(`/discovery/connections/${data.id}`)
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to create connection: ${message}`)
    },
  })
}

export function useUpdateConnection(connectionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateConnectionRequest) =>
      updateConnection(connectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.detail(connectionId),
      })
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      toast.success('Connection updated successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to update connection: ${message}`)
    },
  })
}

export function useDeleteConnection() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (id: string) => deleteConnection(id),
    onSuccess: async (_, id) => {
      queryClient.removeQueries({ queryKey: connectionKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })

      // Cascade: clean up catalog data for this connection (non-blocking)
      try {
        const { del } = await import('@/lib/api-client')
        await del(`/data-tables/by-connection/${id}`)
        queryClient.invalidateQueries({ queryKey: ['dataTables'] })
      } catch {
        // Non-blocking — catalog cleanup failure shouldn't fail deletion
      }

      navigate('/discovery/connections')
      toast.success('Connection deleted successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to delete connection: ${message}`)
    },
  })
}

export function useTestConnection() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => testConnection(id),
    onSuccess: (result, id) => {
      queryClient.invalidateQueries({
        queryKey: connectionKeys.detail(id),
      })
      queryClient.invalidateQueries({ queryKey: connectionKeys.lists() })
      if (result.success) {
        toast.success(`Connection successful (${result.latencyMs}ms)`)
      } else {
        toast.error(`Connection failed: ${result.error ?? 'Unknown error'}`)
      }
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to test connection: ${message}`)
    },
  })
}

// ============================================================================
// SCAN QUERY HOOKS
// ============================================================================

export function useScans(params: ScanQueryParams = {}) {
  const { user } = useAuthStore()
  const tenantId = user?.tenantId ?? ''

  return useQuery({
    queryKey: scanKeys.list(params),
    queryFn: () => fetchScans(params),
    placeholderData: (previousData) => previousData,
    staleTime: 10 * 1000,
    enabled: !!tenantId,
  })
}

export function useScan(id: string | undefined) {
  const normalizedId =
    id && id !== 'undefined' && id !== 'null' && isValidUUID(id) ? id : undefined

  return useQuery({
    queryKey: normalizedId
      ? scanKeys.detail(normalizedId)
      : ['discovery', 'scans', 'detail', 'disabled'],
    queryFn: () => {
      if (!normalizedId) {
        return Promise.reject(new Error('Scan ID is required'))
      }
      return fetchScan(normalizedId)
    },
    enabled: !!normalizedId,
    retry: false,
    refetchInterval: (query) => {
      const scan = query.state.data
      if (scan && (scan.status === 'PENDING' || scan.status === 'RUNNING')) {
        return 3000
      }
      return false
    },
  })
}

export function useScanResults(
  scanId: string | undefined,
  params: ScanResultsQueryParams = {}
) {
  const normalizedId =
    scanId && scanId !== 'undefined' && scanId !== 'null'
      ? scanId
      : undefined

  return useQuery({
    queryKey: normalizedId
      ? scanKeys.results(normalizedId, params)
      : ['discovery', 'scans', 'results', 'disabled'],
    queryFn: () => {
      if (!normalizedId) {
        return Promise.reject(new Error('Scan ID is required'))
      }
      return fetchScanResults(normalizedId, params)
    },
    enabled: !!normalizedId,
    placeholderData: (previousData) => previousData,
    staleTime: 15 * 1000,
  })
}

// ============================================================================
// SCAN MUTATION HOOKS
// ============================================================================

export function useStartScan() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation({
    mutationFn: (data: CreateScanRequest) => startScan(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: scanKeys.lists() })
      toast.success('Scan started successfully')
      navigate(`/discovery/scans/${result.scanId}/results`)
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to start scan: ${message}`)
    },
  })
}

export function useReclassifyScan(scanId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (options: {
      resultIds?: readonly string[]
      enableAiClassification?: boolean
    }) => reclassifyScan(scanId, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scanKeys.detail(scanId) })
      toast.success('Reclassification started')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to reclassify: ${message}`)
    },
  })
}

export function useDeleteScan() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteScan(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: scanKeys.detail(id) })
      queryClient.invalidateQueries({ queryKey: scanKeys.lists() })
      toast.success('Scan deleted successfully')
    },
    onError: (error) => {
      const message = isApiError(error)
        ? error.message
        : 'An unexpected error occurred'
      toast.error(`Failed to delete scan: ${message}`)
    },
  })
}

// ============================================================================
// SOURCE INTROSPECTION HOOKS
// ============================================================================

export function useSchemas(connectionId: string | undefined) {
  return useQuery({
    queryKey: connectionId
      ? connectionKeys.schemas(connectionId)
      : ['discovery', 'schemas', 'disabled'],
    queryFn: () => {
      if (!connectionId) {
        return Promise.reject(new Error('Connection ID is required'))
      }
      return fetchSchemas(connectionId)
    },
    enabled: !!connectionId,
    staleTime: 60 * 1000,
  })
}

export function useTables(
  connectionId: string | undefined,
  schemaName: string | undefined
) {
  const enabled = !!connectionId && !!schemaName

  return useQuery({
    queryKey:
      connectionId && schemaName
        ? connectionKeys.tables(connectionId, schemaName)
        : ['discovery', 'tables', 'disabled'],
    queryFn: () => {
      if (!connectionId || !schemaName) {
        return Promise.reject(new Error('Connection ID and schema are required'))
      }
      return fetchTables(connectionId, schemaName)
    },
    enabled,
    staleTime: 60 * 1000,
  })
}

export function useColumns(
  connectionId: string | undefined,
  schemaName: string | undefined,
  tableName: string | undefined
) {
  const enabled = !!connectionId && !!schemaName && !!tableName

  return useQuery({
    queryKey:
      connectionId && schemaName && tableName
        ? connectionKeys.columns(connectionId, schemaName, tableName)
        : ['discovery', 'columns', 'disabled'],
    queryFn: () => {
      if (!connectionId || !schemaName || !tableName) {
        return Promise.reject(
          new Error('Connection ID, schema, and table are required')
        )
      }
      return fetchColumns(connectionId, schemaName, tableName)
    },
    enabled,
    staleTime: 60 * 1000,
  })
}
